import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  Timestamp,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { recordWorkspaceActivity } from './activityService'
import { queueStockCountRound } from './helper/queueService'
import type { MobileOutboxActorRole, StockCountRoundPayload, StockCountSuggestionDraftPayload, StockScanSuggestionPayload } from './helper/helperOutboxDb'
import { STOCK_COLLECTION, type StockItem } from '../types/stock'
import { toStockItem } from './stockService'
import { callable } from './callable'
import { getSharedDeviceWorkRouterContext } from './sharedDeviceWorkRouter'
import { getActivePersonCache } from './sharedDevicePersonCache'

export type StockCountSuggestionStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED' | 'ESCALATED'
export type StockCountSuggestionKind = 'COUNT' | 'SCAN' | 'RECEIVED'
export type StockCountActorRole = MobileOutboxActorRole

export type StockCountSuggestion = {
  id: string
  workspaceId: string
  kind: StockCountSuggestionKind
  stockItemId: string
  /** Kept for backwards compatibility with existing records */
  itemId?: string
  sku: string
  barcode?: string
  direction?: 'IN' | 'OUT'
  quantity?: number
  countedQuantity: number
  systemQuantityAtScan: number
  actorId: string
  /** Optional while existing suggestions are migrated incrementally. */
  actorRole?: StockCountActorRole
  actorName: string
  taskId?: string | null
  transferId?: string | null
  roundId?: string | null
  locationId?: string | null
  groupKey?: string | null
  status: StockCountSuggestionStatus
  createdAt: Timestamp | null
  /** Captured during approval for audit consumers; older rows may omit it. */
  previousQuantity?: number | null
  reviewedBy?: string | null
  reviewedAt?: Timestamp | null
  movementId?: string | null
  adjustmentId?: string | null
  note?: string | null
}

export type StockCountInventoryItem = {
  sku: string
  barcode: string
  name: string
  quantityOnHand: number
}

const normalizeQuantity = (value: unknown): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : 0
}

const generateRoundId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `stock-count-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const toSuggestion = (id: string, data: Record<string, unknown>): StockCountSuggestion => {
  const kindRaw = String(data.kind ?? '')
  const kind: StockCountSuggestionKind = kindRaw === 'SCAN' || kindRaw === 'RECEIVED' ? kindRaw : 'COUNT'
  const stockItemId = String(data.stockItemId ?? data.itemId ?? data.sku ?? '')
  const quantity = typeof data.quantity === 'number' && Number.isFinite(data.quantity)
    ? normalizeQuantity(data.quantity)
    : undefined
  const countedQuantity = normalizeQuantity(data.countedQuantity ?? quantity ?? 0)

  let status: StockCountSuggestionStatus = 'PENDING'
  if (
    data.status === 'APPROVED' ||
    data.status === 'REJECTED' ||
    data.status === 'SUPERSEDED' ||
    data.status === 'ESCALATED'
  ) {
    status = data.status
  }

  return {
    id,
    workspaceId: String(data.workspaceId ?? ''),
    kind,
    stockItemId,
    itemId: data.itemId ? String(data.itemId) : undefined,
    sku: String(data.sku ?? stockItemId),
    barcode: typeof data.barcode === 'string' ? data.barcode : undefined,
    direction: data.direction === 'IN' || data.direction === 'OUT' ? data.direction : undefined,
    quantity,
    countedQuantity,
    systemQuantityAtScan: normalizeQuantity(data.systemQuantityAtScan),
    actorId: String(data.actorId ?? ''),
    actorRole: data.actorRole === 'MEMBER' ? 'MEMBER' : 'HELPER',
    actorName: String(data.actorName ?? 'Helper'),
    taskId: typeof data.taskId === 'string' ? data.taskId : null,
    transferId: typeof data.transferId === 'string' ? data.transferId : null,
    roundId: typeof data.roundId === 'string' ? data.roundId : null,
    locationId: typeof data.locationId === 'string' ? data.locationId : null,
    groupKey: typeof data.groupKey === 'string' ? data.groupKey : null,
    status,
    createdAt: (data.createdAt as Timestamp | null | undefined) ?? null,
    reviewedBy: typeof data.reviewedBy === 'string' ? data.reviewedBy : null,
    reviewedAt: (data.reviewedAt as Timestamp | null | undefined) ?? null,
    movementId: typeof data.movementId === 'string' ? data.movementId : null,
    adjustmentId: typeof data.adjustmentId === 'string' ? data.adjustmentId : null,
    note: typeof data.note === 'string' ? data.note : null,
  }
}

export const buildStockCountRoundPayload = (input: {
  workspaceId: string
  actor: { id: string; name: string }
  suggestions: StockCountSuggestionDraftPayload[]
  roundId?: string
}): StockCountRoundPayload => ({
  workspaceId: input.workspaceId,
  actorId: input.actor.id,
  actorName: input.actor.name,
  roundId: input.roundId ?? generateRoundId(),
  suggestions: input.suggestions
    .map((entry) => ({
      stockItemId: entry.stockItemId?.trim() || '',
      sku: entry.sku.trim(),
      barcode: entry.barcode.trim(),
      countedQuantity: normalizeQuantity(entry.countedQuantity),
      systemQuantityAtScan: normalizeQuantity(entry.systemQuantityAtScan),
      note: entry.note?.trim() || null,
    }))
    .filter((entry) => entry.sku && entry.barcode && entry.stockItemId),
})

export const submitStockCountRound = async (input: {
  workspaceId: string
  actor: { id: string; name: string }
  suggestions: StockCountSuggestionDraftPayload[]
}) => {
  const payload = buildStockCountRoundPayload(input)
  if (payload.suggestions.length === 0) throw new Error('Add at least one counted item before submitting.')
  return queueStockCountRound(payload)
}

export const buildStockCountRoundDocs = (
  payload: StockCountRoundPayload,
): Array<Record<string, unknown>> =>
  payload.suggestions.map((entry) => ({
    workspaceId: payload.workspaceId,
    kind: 'COUNT',
    stockItemId: entry.stockItemId,
    sku: entry.sku,
    barcode: entry.barcode,
    countedQuantity: normalizeQuantity(entry.countedQuantity),
    systemQuantityAtScan: normalizeQuantity(entry.systemQuantityAtScan),
    actorId: payload.actorId,
    actorName: payload.actorName,
    roundId: payload.roundId,
    status: 'PENDING',
    note: entry.note ?? null,
  }))

export const writeStockCountRound = async (
  payload: StockCountRoundPayload,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const docs = buildStockCountRoundDocs(payload)
  if (docs.length === 0) return
  await runTransaction(db, async (transaction) => {
    docs.forEach((entryDoc) => {
      const suggestionRef = doc(collection(db, 'workspaces', payload.workspaceId, 'stock_count_suggestions'))
      transaction.set(suggestionRef, {
        ...entryDoc,
        createdAt: serverTimestamp(),
      })
    })
  })
  await recordWorkspaceActivity(db, {
    workspaceId: payload.workspaceId,
    actorId: payload.actorId,
    actorName: payload.actorName,
    targetType: 'INVENTORY',
    targetId: payload.roundId,
    targetTitle: 'Stock count round',
    action: 'STOCK_COUNT_SUBMITTED',
    summary: `Helper submitted ${payload.suggestions.length} stock count${payload.suggestions.length === 1 ? '' : 's'}.`,
    metadata: {
      roundId: payload.roundId,
      itemCount: payload.suggestions.length,
      skus: payload.suggestions.map((entry) => entry.sku).slice(0, 20),
    },
  })
}

export const computeScanSuggestionGroupKey = (
  actorId: string,
  taskId?: string | null,
  timestamp: number | Date = Date.now(),
): string => {
  const dateObj = typeof timestamp === 'number' ? new Date(timestamp) : timestamp
  const hour = dateObj.toISOString().slice(0, 13)
  const safeTask = taskId?.trim() ? taskId.trim() : 'notask'
  return `${actorId}__${safeTask}__${hour}`
}

export const buildScanSuggestion = (input: {
  workspaceId: string
  actor: { id: string; name: string; role?: StockCountActorRole }
  stockItemId: string
  sku?: string
  barcode?: string
  direction?: 'IN' | 'OUT'
  quantity?: number
  systemQuantityAtScan?: number
  taskId?: string | null
  locationId?: string | null
  note?: string | null
  timestamp?: number | Date
}): StockScanSuggestionPayload => ({
  workspaceId: input.workspaceId,
  kind: 'SCAN',
  stockItemId: input.stockItemId,
  sku: input.sku ?? input.stockItemId,
  barcode: input.barcode,
  direction: input.direction ?? 'OUT',
  quantity: normalizeQuantity(input.quantity ?? 1),
  countedQuantity: normalizeQuantity(input.quantity ?? 1),
  systemQuantityAtScan: normalizeQuantity(input.systemQuantityAtScan ?? 0),
  actorId: input.actor.id,
  actorName: input.actor.name,
  actorRole: input.actor.role ?? 'HELPER',
  taskId: input.taskId ?? null,
  locationId: input.locationId ?? null,
  groupKey: computeScanSuggestionGroupKey(input.actor.id, input.taskId, input.timestamp),
  note: input.note ?? null,
  createdAtClient: typeof input.timestamp === 'number' ? input.timestamp : input.timestamp instanceof Date ? input.timestamp.getTime() : Date.now(),
})

export const buildReceivedSuggestion = (input: {
  workspaceId: string
  actor: { id: string; name: string; role?: StockCountActorRole }
  transferId: string
  stockItemId: string
  quantity: number
  locationId: string
  sku?: string
  barcode?: string
  taskId?: string | null
  note?: string | null
  timestamp?: number | Date
}): StockScanSuggestionPayload => ({
  workspaceId: input.workspaceId,
  kind: 'RECEIVED',
  transferId: input.transferId,
  stockItemId: input.stockItemId,
  sku: input.sku ?? input.stockItemId,
  barcode: input.barcode,
  quantity: normalizeQuantity(input.quantity),
  countedQuantity: normalizeQuantity(input.quantity),
  systemQuantityAtScan: 0,
  locationId: input.locationId,
  actorId: input.actor.id,
  actorName: input.actor.name,
  actorRole: input.actor.role ?? 'HELPER',
  taskId: input.taskId ?? null,
  groupKey: computeScanSuggestionGroupKey(input.actor.id, input.taskId, input.timestamp),
  note: input.note ?? null,
  createdAtClient: typeof input.timestamp === 'number' ? input.timestamp : input.timestamp instanceof Date ? input.timestamp.getTime() : Date.now(),
})

export type StockScanSuggestionDoc = {
  workspaceId: string
  kind: 'SCAN' | 'COUNT' | 'RECEIVED'
  stockItemId: string
  itemId: string
  sku: string
  barcode: string | null
  direction: 'IN' | 'OUT' | null
  quantity: number
  countedQuantity: number
  systemQuantityAtScan: number
  actorId: string
  actorName: string
  actorRole: StockCountActorRole
  taskId: string | null
  transferId: string | null
  roundId: string | null
  locationId: string | null
  groupKey: string
  status: 'PENDING'
  note: string | null
}

export const buildStockScanSuggestionDoc = (
  payload: StockScanSuggestionPayload,
): StockScanSuggestionDoc => {
  const groupKey = payload.groupKey ?? computeScanSuggestionGroupKey(payload.actorId, payload.taskId)
  const quantity = normalizeQuantity(payload.quantity ?? payload.countedQuantity ?? 1)
  return {
    workspaceId: payload.workspaceId,
    kind: payload.kind ?? 'SCAN',
    stockItemId: payload.stockItemId,
    itemId: payload.stockItemId,
    sku: payload.sku ?? payload.stockItemId,
    barcode: payload.barcode ?? null,
    direction: payload.direction ?? (payload.kind === 'SCAN' ? 'OUT' : null),
    quantity,
    countedQuantity: quantity,
    systemQuantityAtScan: normalizeQuantity(payload.systemQuantityAtScan ?? 0),
    actorId: payload.actorId,
    actorName: payload.actorName,
    actorRole: payload.actorRole ?? 'HELPER',
    taskId: payload.taskId ?? null,
    transferId: payload.transferId ?? null,
    roundId: payload.roundId ?? null,
    locationId: payload.locationId ?? null,
    groupKey,
    status: 'PENDING',
    note: payload.note ?? null,
  }
}

export const writeStockScanSuggestion = async (
  payload: StockScanSuggestionPayload,
  db: Firestore = firestoreDb,
): Promise<string> => {
  const suggestionRef = doc(collection(db, 'workspaces', payload.workspaceId, 'stock_count_suggestions'))
  const docData = buildStockScanSuggestionDoc(payload)
  const data = {
    ...docData,
    createdAt: serverTimestamp(),
  }
  await setDoc(suggestionRef, data)
  await recordWorkspaceActivity(db, {
    workspaceId: payload.workspaceId,
    actorId: payload.actorId,
    actorName: payload.actorName,
    targetType: 'INVENTORY',
    targetId: suggestionRef.id,
    targetTitle: payload.barcode ?? payload.stockItemId,
    action: 'STOCK_SCAN_SUGGESTED',
    summary: `Helper submitted ${payload.kind} suggestion for ${payload.barcode ?? payload.stockItemId}.`,
    metadata: {
      suggestionId: suggestionRef.id,
      kind: payload.kind,
      stockItemId: payload.stockItemId,
      groupKey: docData.groupKey,
    },
  })
  return suggestionRef.id
}

export const listPendingStockCountSuggestions = async (
  workspaceId: string,
  db: Firestore = firestoreDb,
): Promise<StockCountSuggestion[]> => {
  if (!workspaceId) return []
  const snapshot = await getDocs(query(
    collection(db, 'workspaces', workspaceId, 'stock_count_suggestions'),
    where('status', '==', 'PENDING'),
    orderBy('createdAt', 'desc'),
  ))
  return snapshot.docs.map((snap) => toSuggestion(snap.id, snap.data() as Record<string, unknown>))
}

export const subscribePendingStockCountSuggestions = (
  workspaceId: string,
  callback: (suggestions: StockCountSuggestion[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  return onSnapshot(
    query(
      collection(db, 'workspaces', workspaceId, 'stock_count_suggestions'),
      where('status', '==', 'PENDING'),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) => callback(snapshot.docs.map((snap) => toSuggestion(snap.id, snap.data() as Record<string, unknown>))),
    (error) => onError?.(error),
  )
}

export const findStockItemForBarcode = async (
  workspaceId: string,
  code: string,
  db: Firestore = firestoreDb,
): Promise<StockItem | null> => {
  const trimmed = code.trim()
  if (!workspaceId || !trimmed) return null

  // If a shared-device router context is active, resolve strictly from the decrypted person cache
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    const cachedItems = getActivePersonCache()?.snapshot?.stockItems ?? []
    // 1. Match barcode
    const matchBarcode = cachedItems.find((it) => it.barcode?.trim() === trimmed)
    if (matchBarcode) return matchBarcode
    // 2. Match sku
    const matchSku = cachedItems.find((it) => it.sku?.trim() === trimmed)
    if (matchSku) return matchSku
    return null
  }

  // 1. Match workspace_stock by workspaceId + barcode
  const barcodeQuery = query(
    collection(db, STOCK_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('barcode', '==', trimmed),
    limit(1),
  )
  const barcodeSnap = await getDocs(barcodeQuery)
  if (!barcodeSnap.empty) {
    const match = barcodeSnap.docs[0]
    return toStockItem(match.id, match.data())
  }

  // 2. Match workspace_stock by workspaceId + sku
  const skuQuery = query(
    collection(db, STOCK_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('sku', '==', trimmed),
    limit(1),
  )
  const skuSnap = await getDocs(skuQuery)
  if (!skuSnap.empty) {
    const match = skuSnap.docs[0]
    return toStockItem(match.id, match.data())
  }

  return null
}

export const findInventoryForCountBarcode = async (
  workspaceId: string,
  barcode: string,
  db: Firestore = firestoreDb,
): Promise<StockCountInventoryItem | null> => {
  const item = await findStockItemForBarcode(workspaceId, barcode, db)
  if (!item) return null
  return {
    sku: item.sku,
    barcode: item.barcode ?? barcode.trim(),
    name: item.name,
    quantityOnHand: item.quantity,
  }
}

export interface ResolveStockSuggestionsResult {
  resolved: string[]
  skipped: string[]
  results: Array<{
    id: string
    status: 'APPROVED' | 'REJECTED' | 'SKIPPED' | 'PENDING_APPROVAL' | 'FAILED'
    movementId?: string | null
    adjustmentId?: string | null
    reason?: string
    code?: string
    message?: string
  }>
}

export interface UndoStockSuggestionResult {
  undone: boolean
  skippedReason?: 'STALE' | string
  message?: string
}

export interface UndoStockRoundResult {
  undone: number
  skipped: number
  results: Array<{
    id: string
    undone: boolean
    skippedReason?: 'STALE' | string
    message?: string
  }>
}

export const approveStockCountSuggestion = async (input: {
  workspaceId: string
  suggestionId: string
  actor?: { id: string; name: string }
  note?: string | null
  requestId?: string
}): Promise<ResolveStockSuggestionsResult> => {
  const cleanId = input.suggestionId.replace(/[^A-Za-z0-9_-]/g, '_')
  const reqId = input.requestId || `appr_${cleanId}_${Date.now()}`
  return callable<
    { workspaceId: string; ids: string[]; decision: 'APPROVE'; note?: string | null; requestId: string },
    ResolveStockSuggestionsResult
  >('resolveStockSuggestions', {
    workspaceId: input.workspaceId,
    ids: [input.suggestionId],
    decision: 'APPROVE',
    note: input.note ?? null,
    requestId: reqId,
  })
}

export const rejectStockCountSuggestion = async (input: {
  workspaceId: string
  suggestionId: string
  actor?: { id: string; name: string }
  note?: string | null
  requestId?: string
}): Promise<ResolveStockSuggestionsResult> => {
  const cleanId = input.suggestionId.replace(/[^A-Za-z0-9_-]/g, '_')
  const reqId = input.requestId || `rej_${cleanId}_${Date.now()}`
  return callable<
    { workspaceId: string; ids: string[]; decision: 'REJECT'; note?: string | null; requestId: string },
    ResolveStockSuggestionsResult
  >('resolveStockSuggestions', {
    workspaceId: input.workspaceId,
    ids: [input.suggestionId],
    decision: 'REJECT',
    note: input.note ?? null,
    requestId: reqId,
  })
}

export const approveStockCountRound = async (input: {
  workspaceId: string
  roundId: string
  actor?: { id: string; name: string }
  requestId?: string
}, db: Firestore = firestoreDb): Promise<{ approved: number; failed: number; pendingApproval: number; skipped: number; results: ResolveStockSuggestionsResult['results'] }> => {
  const pending = await listPendingStockCountSuggestions(input.workspaceId, db)
  const roundSuggestions = pending.filter(
    (suggestion) => suggestion.roundId === input.roundId && suggestion.status === 'PENDING',
  )
  if (roundSuggestions.length === 0) {
    return { approved: 0, failed: 0, pendingApproval: 0, skipped: 0, results: [] }
  }
  const cleanRoundId = input.roundId.replace(/[^A-Za-z0-9_-]/g, '_')
  const reqId = input.requestId || `appr_rnd_${cleanRoundId}_${Date.now()}`
  const ids = roundSuggestions.map((s) => s.id)
  const res = await callable<
    { workspaceId: string; ids: string[]; decision: 'APPROVE'; requestId: string },
    ResolveStockSuggestionsResult
  >('resolveStockSuggestions', {
    workspaceId: input.workspaceId,
    ids,
    decision: 'APPROVE',
    requestId: reqId,
  })
  const failed = res.results.filter((r) => r.status === 'FAILED').length
  const pendingApproval = res.results.filter((r) => r.status === 'PENDING_APPROVAL').length
  return {
    approved: res.resolved.length,
    failed,
    pendingApproval,
    skipped: res.skipped.length,
    results: res.results,
  }
}

export const undoStockSuggestion = async (input: {
  workspaceId: string
  suggestionId: string
}): Promise<UndoStockSuggestionResult> => {
  return callable<
    { workspaceId: string; suggestionId: string },
    UndoStockSuggestionResult
  >('undoStockSuggestion', {
    workspaceId: input.workspaceId,
    suggestionId: input.suggestionId,
  })
}

export const undoStockRound = async (input: {
  workspaceId: string
  roundId: string
}): Promise<UndoStockRoundResult> => {
  return callable<
    { workspaceId: string; roundId: string },
    UndoStockRoundResult
  >('undoStockRound', {
    workspaceId: input.workspaceId,
    roundId: input.roundId,
  })
}

export interface RecentStockApproval {
  id: string
  kind: 'SUGGESTION' | 'ROUND'
  roundId?: string | null
  suggestionId?: string
  sku?: string
  itemName?: string
  approverId: string
  approvedAt: number
  count?: number
}

export const subscribeMyRecentStockApprovals = (
  workspaceId: string,
  uid: string,
  callback: (approvals: RecentStockApproval[]) => void,
  onError?: (err: Error) => void,
  db: Firestore = firestoreDb,
): Unsubscribe => {
  if (!workspaceId || !uid) {
    callback([])
    return () => {}
  }

  const fiveMinutesAgo = Timestamp.fromMillis(Date.now() - 5 * 60 * 1000)
  const q = query(
    collection(db, 'workspaces', workspaceId, 'stock_count_suggestions'),
    where('status', '==', 'APPROVED'),
    where('reviewedBy', '==', uid),
    where('reviewedAt', '>=', fiveMinutesAgo),
    orderBy('reviewedAt', 'desc'),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Array<Record<string, unknown> & { id: string }>
      const roundGroups = new Map<string, Array<Record<string, unknown> & { id: string }>>()
      const singleItems: RecentStockApproval[] = []

      for (const item of docs) {
        const roundId = typeof item.roundId === 'string' && item.roundId.trim() ? item.roundId.trim() : null
        if (roundId) {
          const list = roundGroups.get(roundId) ?? []
          list.push(item)
          roundGroups.set(roundId, list)
        } else {
          const reviewedAtMs = item.reviewedAt && typeof (item.reviewedAt as { toMillis?: () => number }).toMillis === 'function'
            ? (item.reviewedAt as { toMillis: () => number }).toMillis()
            : (item.reviewedAt instanceof Date ? item.reviewedAt.getTime() : Date.now())
          singleItems.push({
            id: `suggestion-${item.id}`,
            kind: 'SUGGESTION',
            suggestionId: item.id,
            sku: typeof item.sku === 'string' ? item.sku : undefined,
            approverId: uid,
            approvedAt: reviewedAtMs,
          })
        }
      }

      const approvals: RecentStockApproval[] = []
      for (const [roundId, roundDocs] of roundGroups.entries()) {
        let maxReviewedAt = 0
        for (const doc of roundDocs) {
          const ms = doc.reviewedAt && typeof (doc.reviewedAt as { toMillis?: () => number }).toMillis === 'function'
            ? (doc.reviewedAt as { toMillis: () => number }).toMillis()
            : (doc.reviewedAt instanceof Date ? doc.reviewedAt.getTime() : 0)
          if (ms > maxReviewedAt) maxReviewedAt = ms
        }
        approvals.push({
          id: `round-${roundId}`,
          kind: 'ROUND',
          roundId,
          approverId: uid,
          approvedAt: maxReviewedAt || Date.now(),
          count: roundDocs.length,
        })
      }

      approvals.push(...singleItems)
      approvals.sort((a, b) => b.approvedAt - a.approvedAt)
      callback(approvals)
    },
    (error) => {
      onError?.(error)
    },
  )
}
